import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Users } from "lucide-react";
import type { WorkspaceInvitePreview } from "@hub/shared";
import { Button, EmptyState, Spinner, toast } from "@/ui";
import { api, ApiError } from "@/api/client";
import { useSession } from "@/app/session";

/**
 * /invite/:token, where a workspace invitation lands: the emailed https link in a browser, or hub://invite/<token> in
 * the Mac app. Says whose workspace it is and joins it; only the invited address can, so another account is told
 * which address to sign in with.
 */
export function InvitePage() {
  const { token = "" } = useParams();
  const session = useSession();
  const [preview, setPreview] = useState<WorkspaceInvitePreview | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    api.workspaces
      .preview(token)
      .then(setPreview)
      .catch((e: unknown) => setFailure(e instanceof ApiError && e.status === 404 ? "This invitation link is not valid. Ask for a new one." : e instanceof Error ? e.message : "Could not open the invitation"));
  }, [token]);

  /** Switch to the workspace and reload, as Settings does, so the engine opens it and pulls its people. */
  const open = async (workspaceId: string) => {
    await session.refreshMe();
    session.setWorkspace(workspaceId);
    window.location.assign("/");
  };

  const join = async () => {
    setJoining(true);
    try {
      const res = await api.workspaces.acceptInvite({ token });
      toast(`You joined ${res.workspace.name}`, "success");
      await open(res.workspace.id);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Could not join the workspace", "danger");
      setJoining(false);
    }
  };

  if (failure) return <EmptyState icon={<Users size={28} />} title="Invitation not found">{failure}</EmptyState>;
  if (!preview) return <EmptyState title="Opening the invitation" icon={<Spinner />} />;

  if (preview.alreadyMember) {
    return (
      <EmptyState icon={<Users size={28} />} title={`You are in ${preview.workspaceName}`} action={<Button variant="primary" onClick={() => open(preview.workspaceId)}>Open {preview.workspaceName}</Button>}>
        The workspace menu at the top of the sidebar switches between your workspaces.
      </EmptyState>
    );
  }
  if (preview.accepted) return <EmptyState icon={<Users size={28} />} title="This invitation was already used">Ask {preview.inviterName} for a new one.</EmptyState>;
  if (!preview.forYou) {
    return (
      <EmptyState icon={<Users size={28} />} title="This invitation is for another address" action={<Button onClick={() => session.signOut()}>Sign out</Button>}>
        {preview.inviterName} invited {preview.email} to {preview.workspaceName}. You are signed in as {session.user?.email}. Sign out, then sign in with {preview.email} and open the link again.
      </EmptyState>
    );
  }
  return (
    <EmptyState icon={<Users size={28} />} title={`${preview.inviterName} invited you to ${preview.workspaceName}`} action={<Button variant="primary" loading={joining} onClick={join}>Join {preview.workspaceName}</Button>}>
      You will share its people, groups and notes. Your own workspace stays as it is, and the workspace menu switches between them.
    </EmptyState>
  );
}
